import { Product, ProductType, SETTLEMENT_TIME_MAP, SettlementTimeKey } from '@protos/product';
import { addBusinessDays, addMilliseconds, addMinutes, format, parse, parseISO } from 'date-fns';
import { formatInTimeZone, getTimezoneOffset } from 'date-fns-tz';

const LONDON_TIMEZONE = 'Europe/London';

export const DATETIME_FORMAT = 'dd.MM.yyy HH:mm:ss';
export const DATETIME_FORMAT_YYYY_MM_DD = 'yyy-MM-dd HH:mm:ss';
export const DATETIME_FORMAT_WITH_MILLISECONDS = 'dd/MM/yyyy HH:mm:ss:SSS';

export const formatDatetime = (date: string) => format(parseISO(date), DATETIME_FORMAT);

export const formatDatetimeWithMilliseconds = (date?: string) => (date ? format(parseISO(date), DATETIME_FORMAT_WITH_MILLISECONDS) : '');

export const formatDatetimeYYYYMMDD = (date: string) => format(parseISO(date), DATETIME_FORMAT_YYYY_MM_DD);

export const formatToTimezone = (date: Date, timezone: string, formatStr = 'yyyy-MM-dd HH:mm:ss'): Date => {
  const zonedDate = formatInTimeZone(date, timezone, formatStr);
  return new Date(zonedDate);
};

export const formatDateTimeUTC = (date: string) => formatInTimeZone(date, 'UTC', DATETIME_FORMAT);

export const isLondonTimeZone = () => {
  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return localTimezone === LONDON_TIMEZONE;
};

export const getLondonEndOfDayLabel = (date: Date, dateFormat = 'dd MMM yyyy'): string | undefined => {
  const londonDate = formatToTimezone(date, LONDON_TIMEZONE);
  return `(London: ${format(londonDate, dateFormat)} EOD)`;
};

export const getDateTimeDifference = (date1: string | number, date2: string | number): string => {
  const diff = Math.abs(new Date(date1).getTime() - new Date(date2).getTime());
  const diffMinutes = Math.ceil(diff / (1000 * 60));

  if (diffMinutes < 1) return '<1m';
  if (diffMinutes >= 60 && diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h`;
  if (diffMinutes >= 1440) return `${Math.floor(diffMinutes / 1440)}d`;

  return `${diffMinutes}m`;
};

export const getDateInLondonTimezone = (date: string, format = 'yyyy-MM-dd HH:mm:ss'): string => {
  const attachedDate = parse(date, format, new Date());
  const offset = getTimezoneOffset(LONDON_TIMEZONE);
  const newDate = addMilliseconds(attachedDate, offset);

  return formatInTimeZone(newDate, LONDON_TIMEZONE, format);
};

export const getNow = () => new Date().toISOString();

export const getYesterday = (date: string) => addBusinessDays(new Date(date), -1).toISOString();

export const getStartOfDay = (date: string) => format(parseISO(date), 'yyyy-MM-dd 00:00:00');

export const getEndOfDay = (date: string) => format(parseISO(date), 'yyyy-MM-dd 23:59:59');

export const addMinutesToDateTime = (date: string, value: number) => addMinutes(new Date(date), value);

export const getDateYYYYMMDD = (date: Date) =>
  date.getFullYear() +
  '-' +
  (date.getMonth() < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1) +
  '-' +
  (date.getDate() < 10 ? `0${date.getDate()}` : date.getDate());

export const toLocaleISOString = (date: Date) => {
  const pad = (number: number) => (number < 10 ? '0' + number : number);

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1); // Months are zero-based
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  const milliseconds = (date.getMilliseconds() / 1000).toFixed(3).slice(2, 5);

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
};

export const addSecondsToNow = (seconds: number): string => {
  const now = Date.now();
  const futureTimestamp = now + seconds * 1000;

  return new Date(futureTimestamp).toISOString();
};

export const getTimeDifferenceGranular = (date1: string, date2: string): string => {
  const diffMilliseconds = Math.abs(new Date(date1).getTime() - new Date(date2).getTime());
  const diffSeconds = Math.floor(diffMilliseconds / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMilliseconds < 1000) return `${diffMilliseconds}ms`;
  if (diffSeconds < 60) return `${diffSeconds}s`;
  if (diffMinutes < 60) return `${diffMinutes}m`;
  if (diffHours < 24) return `${diffHours}h`;
  return `${diffDays}d`;
};

const parseUTCDateTime = (dateStr: string, timeStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const timeParts = timeStr.split(':');
  const hours = Number(timeParts[0]);
  const minutes = Number(timeParts[1]);
  const seconds = timeParts[2] ? Number(timeParts[2]) : 0;
  return new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
};

const getUTCDateString = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isDateInvalidForMOCLadder = (date: Date, marketClose: SettlementTimeKey | undefined): boolean => {
  if (!marketClose) {
    return false;
  }

  const settlementTime =
    marketClose === 'unspecified' ? SETTLEMENT_TIME_MAP.utc_1630.time : SETTLEMENT_TIME_MAP[marketClose as SettlementTimeKey]?.time;

  if (!settlementTime) {
    return false;
  }

  const now = new Date();
  const today = getUTCDateString(now);
  const dateDay = getUTCDateString(date);

  if (dateDay !== today) {
    return true;
  }

  const settlementTimeDate = parseUTCDateTime(dateDay, settlementTime);
  const tenMinsBeforeMarketClose = new Date(settlementTimeDate.getTime() - 10 * 60 * 1000);
  const isPastSettlementTime = date.getTime() > tenMinsBeforeMarketClose.getTime();

  return isPastSettlementTime;
};

export const wouldExpireAfterSettlementTime = (product: Product | undefined, expiryTimestamp: string): boolean => {
  if (!product || product.product_type !== ProductType.MOC) {
    return false;
  }

  const expiryDate = new Date(expiryTimestamp);

  return product.exchange_symbols.some(exchangeSymbol => isDateInvalidForMOCLadder(expiryDate, exchangeSymbol.market_close));
};

export const isAfterSettlementTime = (product: Product | undefined, exchange?: string): boolean => {
  if (!product || product.product_type !== ProductType.MOC) {
    return false;
  }

  const now = new Date();

  if (exchange) {
    const productSettlementTime = product.exchange_symbols.find(ex => ex.exchange === exchange.toLowerCase())?.market_close;
    return isDateInvalidForMOCLadder(now, productSettlementTime);
  }

  return product.exchange_symbols.some(exchangeSymbol => isDateInvalidForMOCLadder(now, exchangeSymbol.market_close));
};

export const getMaxValidityBeforeSettlement = (product: Product | undefined): number | null => {
  if (!product || product.product_type !== ProductType.MOC || !product.exchange_symbols.length) {
    return null;
  }

  const now = new Date();
  const today = getUTCDateString(now);

  let earliestCutoffTime: Date | null = null;

  for (const exchangeSymbol of product.exchange_symbols) {
    const marketClose = exchangeSymbol.market_close;
    if (!marketClose) continue;

    const settlementTime =
      marketClose === 'unspecified' ? SETTLEMENT_TIME_MAP.utc_1630.time : SETTLEMENT_TIME_MAP[marketClose as SettlementTimeKey]?.time;

    if (!settlementTime) continue;

    const settlementTimeDate = parseUTCDateTime(today, settlementTime);
    const tenMinsBeforeMarketClose = new Date(settlementTimeDate.getTime() - 10 * 60 * 1000);

    if (tenMinsBeforeMarketClose.getTime() > now.getTime()) {
      if (!earliestCutoffTime || tenMinsBeforeMarketClose.getTime() < earliestCutoffTime.getTime()) {
        earliestCutoffTime = tenMinsBeforeMarketClose;
      }
    }
  }

  if (!earliestCutoffTime) {
    return null;
  }

  const diffMs = earliestCutoffTime.getTime() - now.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);

  return diffSeconds > 0 ? diffSeconds : null;
};
